import { db } from '../lib/firebase';
import { collection, doc, setDoc, Timestamp } from 'firebase/firestore';
import { Activity } from '../types/activities';

const getCategoryImage = (category: string): string => {
  const imageMap: Record<string, string> = {
    'hiking': '/activity-hiking.webp',
    'sports': '/activity-sports.webp',
    'food': '/activity-food.webp',
    'cultural': '/activity-culture.webp',
    'nightlife': '/activity-nightlife.webp',
    'beach': '/activity-beach.webp',
    'nature': '/activity-nature.webp',
    'roadtrip': '/activity-nature.webp',
    'language-exchange': '/activity-culture.webp',
  };
  return imageMap[category] || '/activity-nature.webp';
};

const DEMO_ACTIVITIES = [
  // AUSTRALIE
  {
    title: 'Randonnée au Blue Mountains National Park',
    description: "Rejoignez-nous pour une journée de randonnée dans les magnifiques Blue Mountains ! Nous allons explorer le Three Sisters trail et profiter des vues spectaculaires sur les eucalyptus. Niveau modéré, prévoir de bonnes chaussures et de l'eau. Pique-nique au sommet !",
    category: 'hiking' as const,
    city: 'Sydney',
    country: 'Australie',
    countryCode: 'AU',
    date: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
    time: '08:30',
    maxParticipants: 8,
    meetingPoint: 'Central Station, Platform 1',
    difficulty: 'moderate' as const,
    budget: 'cheap' as const,
    spokenLanguages: ['fr', 'en'],
    requirements: 'Bonnes chaussures de marche, eau, snacks'
  },
  {
    title: 'Soirée BBQ à Bondi Beach',
    description: "BBQ australien typique sur la plage de Bondi ! Apportez vos grillades et vos boissons, on s'occupe du reste. Coucher de soleil, musique et bonne ambiance garantis. Parfait pour rencontrer d'autres backpackers !",
    category: 'nightlife' as const,
    city: 'Sydney',
    country: 'Australie',
    countryCode: 'AU',
    date: new Date(Date.now() + 8 * 24 * 60 * 60 * 1000),
    time: '17:00',
    maxParticipants: 15,
    meetingPoint: 'Bondi Beach, près du skate park',
    difficulty: 'easy' as const,
    budget: 'cheap' as const,
    spokenLanguages: ['fr', 'en'],
  },
  {
    title: 'Road trip Great Ocean Road',
    description: "Week-end épique sur la Great Ocean Road ! Départ vendredi matin, retour dimanche soir. On va voir les 12 Apôtres, s'arrêter dans des spots de surf, et peut-être apercevoir des koalas. Frais d'essence et hébergement partagés.",
    category: 'roadtrip' as const,
    city: 'Melbourne',
    country: 'Australie',
    countryCode: 'AU',
    date: new Date(Date.now() + 12 * 24 * 60 * 60 * 1000),
    time: '07:00',
    maxParticipants: 4,
    meetingPoint: 'Southern Cross Station',
    difficulty: 'easy' as const,
    budget: 'moderate' as const,
    spokenLanguages: ['fr', 'en'],
    requirements: 'Permis de conduire international'
  },

  // CANADA
  {
    title: 'Randonnée Grouse Grind',
    description: "Le fameux 'Mother Nature's Stairmaster' ! 2.9 km avec 853m de dénivelé. Challenge accepté ? Vue incroyable sur Vancouver au sommet. On redescend en télécabine. Niveau sportif, préparez-vous à souffrir un peu 😅",
    category: 'hiking' as const,
    city: 'Vancouver',
    country: 'Canada',
    countryCode: 'CA',
    date: new Date(Date.now() + 6 * 24 * 60 * 60 * 1000),
    time: '09:00',
    maxParticipants: 6,
    meetingPoint: 'Grouse Mountain parking',
    difficulty: 'challenging' as const,
    budget: 'cheap' as const,
    spokenLanguages: ['fr', 'en'],
    requirements: 'Excellente condition physique'
  },
  {
    title: 'Brunch et visite du Vieux-Montréal',
    description: "Découverte gourmande du Vieux-Montréal ! On commence par un brunch dans un resto typique, puis balade dans les rues pavées, Basilique Notre-Dame, et le Vieux-Port. Ambiance chill et conviviale !",
    category: 'food' as const,
    city: 'Montréal',
    country: 'Canada',
    countryCode: 'CA',
    date: new Date(Date.now() + 4 * 24 * 60 * 60 * 1000),
    time: '11:00',
    maxParticipants: 8,
    meetingPoint: 'Station de métro Place-d\'Armes',
    difficulty: 'easy' as const,
    budget: 'moderate' as const,
    spokenLanguages: ['fr', 'en'],
  },
  {
    title: 'Soirée Hockey des Canadiens',
    description: "Expérience canadienne ultime : match des Canadiens au Centre Bell ! Ambiance de fou, on va encourager nos Habs ensemble. Billets à prix raisonnable dans les gradins supérieurs. Go Habs Go !",
    category: 'cultural' as const,
    city: 'Montréal',
    country: 'Canada',
    countryCode: 'CA',
    date: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000),
    time: '19:00',
    maxParticipants: 6,
    meetingPoint: 'Centre Bell, entrée principale',
    difficulty: 'easy' as const,
    budget: 'moderate' as const,
    spokenLanguages: ['fr', 'en'],
  },

  // NOUVELLE-ZÉLANDE
  {
    title: 'Tongariro Alpine Crossing',
    description: "L'une des plus belles randonnées du monde ! 19.4 km à travers des paysages volcaniques époustouflants. Lacs émeraude, cratères, vues sur le Mont Doom du Seigneur des Anneaux. Départ tôt le matin, journée complète.",
    category: 'hiking' as const,
    city: 'Taupo',
    country: 'Nouvelle-Zélande',
    countryCode: 'NZ',
    date: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000),
    time: '06:00',
    maxParticipants: 6,
    meetingPoint: 'Taupo i-SITE Visitor Centre',
    difficulty: 'challenging' as const,
    budget: 'moderate' as const,
    spokenLanguages: ['fr', 'en'],
    requirements: 'Bonnes chaussures, vêtements chauds, crème solaire'
  },
  {
    title: 'Visite de Hobbiton',
    description: "Visite guidée du village des Hobbits ! Pour les fans du Seigneur des Anneaux et du Hobbit. On explore le Shire, les trous de Hobbit, et on finit par une bière au Green Dragon. Magique !",
    category: 'cultural' as const,
    city: 'Matamata',
    country: 'Nouvelle-Zélande',
    countryCode: 'NZ',
    date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    time: '13:00',
    maxParticipants: 8,
    meetingPoint: 'Matamata i-SITE',
    difficulty: 'easy' as const,
    budget: 'expensive' as const,
    spokenLanguages: ['fr', 'en'],
  },

  // JAPON
  {
    title: 'Randonnée au Mont Takao',
    description: "Escapade nature à 1h de Tokyo ! Le Mont Takao offre plusieurs sentiers avec des temples, une forêt magnifique et une vue sur le Mont Fuji par temps clair. Parfait pour débutants. Déjeuner au sommet !",
    category: 'hiking' as const,
    city: 'Tokyo',
    country: 'Japon',
    countryCode: 'JP',
    date: new Date(Date.now() + 9 * 24 * 60 * 60 * 1000),
    time: '09:30',
    maxParticipants: 10,
    meetingPoint: 'Gare de Takaosanguchi',
    difficulty: 'easy' as const,
    budget: 'cheap' as const,
    spokenLanguages: ['fr', 'en', 'ja'],
  },
  {
    title: 'Échange linguistique Français-Japonais',
    description: "Rencontre conviviale pour pratiquer le français et le japonais ! On se retrouve dans un café à Shibuya, moitié du temps en français, moitié en japonais. Tous niveaux bienvenus. L'occasion de se faire des amis locaux !",
    category: 'language-exchange' as const,
    city: 'Tokyo',
    country: 'Japon',
    countryCode: 'JP',
    date: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
    time: '19:00',
    maxParticipants: 12,
    meetingPoint: 'Starbucks Shibuya Crossing',
    difficulty: 'easy' as const,
    budget: 'cheap' as const,
    spokenLanguages: ['fr', 'ja', 'en'],
  },
  {
    title: 'Food tour à Dotonbori',
    description: "Découverte culinaire d'Osaka ! Takoyaki, okonomiyaki, kushikatsu... on goûte toutes les spécialités locales dans le quartier mythique de Dotonbori. Préparez vos estomacs et vos appareils photo !",
    category: 'food' as const,
    city: 'Osaka',
    country: 'Japon',
    countryCode: 'JP',
    date: new Date(Date.now() + 11 * 24 * 60 * 60 * 1000),
    time: '18:00',
    maxParticipants: 8,
    meetingPoint: 'Glico Man sign, Dotonbori',
    difficulty: 'easy' as const,
    budget: 'moderate' as const,
    spokenLanguages: ['fr', 'en', 'ja'],
  },

  // ARGENTINE
  {
    title: 'Trek au Fitz Roy',
    description: "Randonnée mythique en Patagonie ! Le trek jusqu'au Fitz Roy offre des paysages à couper le souffle. 8-10h de marche, niveau sportif mais ça en vaut la peine. Départ très tôt pour profiter de la lumière du matin.",
    category: 'hiking' as const,
    city: 'El Chaltén',
    country: 'Argentine',
    countryCode: 'AR',
    date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
    time: '06:00',
    maxParticipants: 6,
    meetingPoint: 'Centre-ville El Chaltén',
    difficulty: 'challenging' as const,
    budget: 'free' as const,
    spokenLanguages: ['fr', 'es', 'en'],
    requirements: 'Excellente condition physique, équipement de randonnée'
  },
  {
    title: 'Soirée tango et empanadas',
    description: "Immersion dans la culture argentine ! Cours de tango pour débutants suivi d'un dîner empanadas et vino tinto. Ambiance chaleureuse dans une milonga traditionnelle de San Telmo. Aucune expérience nécessaire !",
    category: 'cultural' as const,
    city: 'Buenos Aires',
    country: 'Argentine',
    countryCode: 'AR',
    date: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
    time: '20:00',
    maxParticipants: 10,
    meetingPoint: 'Plaza Dorrego, San Telmo',
    difficulty: 'easy' as const,
    budget: 'cheap' as const,
    spokenLanguages: ['fr', 'es', 'en'],
  },
];

export async function seedActivities(userId: string, username: string) {
  if (userId !== '1') {
    throw new Error('Accès non autorisé');
  }

  let createdCount = 0;

  for (const activityData of DEMO_ACTIVITIES) {
    try {
      const activityRef = doc(collection(db, 'activities'));

      const activity = {
        id: activityRef.id,
        userId,
        username: 'Pvtistes Team',
        title: activityData.title,
        description: activityData.description,
        category: activityData.category,
        date: Timestamp.fromDate(activityData.date),
        time: activityData.time,
        city: activityData.city,
        country: activityData.country,
        countryCode: activityData.countryCode,
        meetingPoint: activityData.meetingPoint,
        maxParticipants: activityData.maxParticipants,
        difficulty: activityData.difficulty,
        budget: activityData.budget,
        spokenLanguages: activityData.spokenLanguages,
        requirements: activityData.requirements || '',
        imageUrl: getCategoryImage(activityData.category),
        participants: [{
          userId,
          username: 'Pvtistes Team',
          joinedAt: Timestamp.now()
        }],
        comments: [],
        likes: [],
        favorites: [],
        status: 'open',
        isActive: true,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(activityRef, activity);
      createdCount++;
      console.log(`✅ Activité créée: ${activity.title}`);
    } catch (error) {
      console.error(`❌ Erreur pour l'activité ${activityData.title}:`, error);
    }
  }

  console.log(`\n🎉 ${createdCount}/${DEMO_ACTIVITIES.length} activités créées avec succès !`);
  return createdCount;
}
